// ---------------------------------------------------------------------------
// Update-existing-work — the ONE place "I want to change what I already made"
// becomes a resolved project instead of a sentence handed to a model.
//
// WHY THIS EXISTS — production defect, 2026-08-04 (www.cogo.xyz/studio, real
// account). PR #2020 taught the "Update existing work" chip to carry the
// workspace's project names, which fixed the assistant asking "which app?".
// But carrying context is not the same as binding intent: the chip's message
// still fell through EVERY client-side detector in `chat-model-adapter.run`
// (`isCurrentContextInspection`, the two SIPASS paths,
// `detectApplicationImportIntent`, `detectLiveCogoSpecIntent`,
// `detectClientDeterministicIntent` — measured: all null/false) and reached
// the gateway as free text. Nothing in that text constrains the ACTION, so the
// model was free to choose generation, and it did:
//
//     user  → Update existing work. My existing work in this workspace:
//             "Woori Branch Finder Rebuild", …
//     model → 즉시 앱을 생성할게요! 🏦
//             [MCP Error: Invalid input for Studio operation …]
//
// A project literally named "… Rebuild" reads as a build brief, so the very
// context that was added to stop the wrong QUESTION invited the wrong VERB.
// An update request must not be a suggestion to a model — it is a lookup the
// client can already perform, because the client owns `userProjects`.
//
// Recognition is anchored on the EXACT localized chip phrases rather than a
// natural-language "update"-ish regex. That is deliberate and follows the
// precedent set when the chat→knowledge auto-trigger was removed (see the
// removal note in chat-model-adapter.ts): a loose classifier over ordinary
// studio prose false-positived on 4 of 10 realistic messages, and a wrong
// claim here does not merely fail — it hijacks a request the model would have
// handled correctly. Exact phrases cannot do that.
// ---------------------------------------------------------------------------

import type { SingleActionIntentResponse } from './mock-chat-service'

/**
 * The "update existing work" chip, in every locale this app ships. Matching on
 * the localized string (not an index) keeps the behavior attached to the
 * chip's MEANING — reordering `strings.chat.suggestions` can't silently move
 * it onto "build a reservation app".
 */
const UPDATE_EXISTING_WORK_CHIPS = new Set([
  'Update existing work',
  '기존 작업을 수정하고 싶어',
  '既存の作業を更新',
  '更新现有工作',
  '更新現有工作',
])

export function isUpdateExistingWorkChip(text: string): boolean {
  return UPDATE_EXISTING_WORK_CHIPS.has(text.trim())
}

/**
 * True when `text` IS an update-existing-work request — the bare chip, or the
 * chip followed by the workspace context `buildUpdateExistingWorkPrompt`
 * appends. Anchored at the start so a message that merely quotes the phrase
 * mid-sentence is left to the model.
 */
export function startsWithUpdateExistingWorkChip(text: string): boolean {
  const trimmed = text.trim()
  for (const chip of UPDATE_EXISTING_WORK_CHIPS) {
    if (trimmed === chip) return true
    // The builder joins with ". "; a locale that ends its chip with its own
    // punctuation is covered by the equality check above.
    if (trimmed.startsWith(`${chip}.`)) return true
  }
  return false
}

/** The project fields this module reads. Structural on purpose — the caller
 * passes `ProjectMeta` rows, and tests pass the same shape without importing
 * the full server type. */
export interface UpdateExistingWorkProject {
  id?: string | null
  name?: string | null
  updated_at?: string | null
}

/** One distinct piece of existing work the user can be offered. */
export interface UpdateExistingWorkTarget {
  /** Project id, when the row carried one — required to actually open it. */
  id: string | null
  name: string
  /** `YYYY-MM-DD`, or null when the row had no parsable date. Never invented. */
  updatedOn: string | null
  /** Epoch ms used for ordering; `null` sorts last. */
  updatedAt: number | null
}

/** How many choices may reach the prompt. Applied AFTER de-duplication, so a
 * workspace of same-named projects cannot crowd out its distinct work. */
const MAX_LISTED_TARGETS = 5

function parseUpdatedAt(value: string | null | undefined): number | null {
  if (typeof value !== 'string' || !value.trim()) return null
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}

function isoDay(epochMs: number | null): string | null {
  if (epochMs === null) return null
  return new Date(epochMs).toISOString().slice(0, 10)
}

/**
 * Collapse the workspace read into DISTINCT, ordered choices.
 *
 * De-duplicated by display name (trimmed, case- and width-insensitive via NFC
 * + lowercase) keeping the most recently updated row for each name, then
 * ordered most-recent first, then capped. The production account that exposed
 * this held four rows named "Woori Branch Finder Rebuild"; without this the
 * user was offered the same string four times and the cap hid everything else.
 */
export function resolveUpdateExistingWorkTargets(
  projects: readonly UpdateExistingWorkProject[],
): UpdateExistingWorkTarget[] {
  const byName = new Map<string, UpdateExistingWorkTarget>()

  for (const project of projects) {
    const name = project.name?.trim()
    if (!name) continue
    const key = name.normalize('NFC').toLowerCase()
    const updatedAt = parseUpdatedAt(project.updated_at)
    const candidate: UpdateExistingWorkTarget = {
      id: project.id?.trim() || null,
      name,
      updatedOn: isoDay(updatedAt),
      updatedAt,
    }
    const existing = byName.get(key)
    if (!existing) {
      byName.set(key, candidate)
      continue
    }
    // Keep the freshest row for this name. An undated row never displaces a
    // dated one, so the choice offered is the one the user touched last.
    const existingAt = existing.updatedAt ?? -Infinity
    const candidateAt = updatedAt ?? -Infinity
    if (candidateAt > existingAt) byName.set(key, candidate)
  }

  return [...byName.values()]
    .sort((a, b) => (b.updatedAt ?? -Infinity) - (a.updatedAt ?? -Infinity))
    .slice(0, MAX_LISTED_TARGETS)
}

/** `"Name" (updated 2026-08-04)`, or bare `"Name"` when there is no date. */
export function formatUpdateExistingWorkTarget(
  target: UpdateExistingWorkTarget,
): string {
  return target.updatedOn
    ? `"${target.name}" (updated ${target.updatedOn})`
    : `"${target.name}"`
}

/**
 * The message the chip sends.
 *
 * Still natural language, because the gateway is the fallback when the
 * deterministic path below cannot resolve a target — but every listed choice
 * is now distinct, so "ask me which one only if it is ambiguous" is a question
 * the assistant can actually act on.
 */
export function buildUpdateExistingWorkPrompt(
  chipText: string,
  projects: readonly UpdateExistingWorkProject[],
): string {
  const targets = resolveUpdateExistingWorkTargets(projects)
  if (targets.length === 0) return chipText
  return `${chipText}. My existing work in this workspace: ${targets
    .map(formatUpdateExistingWorkTarget)
    .join(', ')}. Ask me which one only if it is ambiguous.`
}

/** Authored, localized copy this intent renders. Passed in so the module stays
 * pure and testable without the i18n runtime. */
export interface UpdateExistingWorkCopy {
  /** Nothing saved yet — offer creation as the next step, never start it. */
  noExistingWork: string
  /** `{name}` is substituted with the resolved project name. */
  opening: string
  /** Several distinct pieces of work — ask, listing them. */
  chooseOne: string
}

/**
 * What the client decided, and what the surface must therefore render.
 *
 * `choices` is non-empty EXACTLY when `response` asks a question the user has
 * to answer. Returning both from one call is deliberate: when the branch that
 * asks and the branch that renders the answer live in two files they drift,
 * and the drift is invisible — the question still appears, the rows just stop
 * coming. `pairs the question with its rows` in the unit tests pins it.
 */
export interface UpdateExistingWorkOutcome {
  response: SingleActionIntentResponse
  /** Rows to render so the answer is a click, not a parse. */
  choices: UpdateExistingWorkTarget[]
}

/**
 * Resolve an update request to an action, or to a question. NEVER to
 * generation.
 *
 * - exactly one distinct piece of work → open it (`openProject`, a readOnly
 *   navigation tool, so `executeCliAction` runs it without an operation
 *   contract). No choices — nothing left to pick.
 * - several → ask which, listing the distinct choices, and hand those choices
 *   back so the surface can render them as rows. No action runs yet.
 * - none, or the single match has no id → say so. No action, no choices.
 *
 * Returns `null` when the message is not an update request, leaving every
 * other detector and the gateway exactly as they were.
 *
 * WHY `choices` EXISTS — production defect, 2026-08-04 (measured on
 * www.cogo.xyz/studio, account with 5 distinct projects). The "several"
 * branch below asked "Which one should I open?" and stopped. The user's
 * answer — a bare project name — starts with no chip phrase, so
 * `startsWithUpdateExistingWorkChip` rejected it, every other detector missed
 * it, and it reached the gateway as free text. The model replied "Great
 * choice! Let me load the app" and called nothing; 36 seconds later the route
 * was still /studio. The client asked the question, so the client has to own
 * the answer — and it already holds the ids, so the answer should never have
 * been a string to re-resolve.
 *
 * The two e2e fixtures both stepped around this: four same-named rows collapse
 * to ONE distinct target (the open branch), and the two-distinct case asserts
 * the question and deliberately stops there. Only a workspace with several
 * distinct projects reaches the gap.
 */
export function detectUpdateExistingWorkIntent(
  content: string,
  projects: readonly UpdateExistingWorkProject[],
  copy: UpdateExistingWorkCopy,
): UpdateExistingWorkOutcome | null {
  if (!startsWithUpdateExistingWorkChip(content)) return null

  const targets = resolveUpdateExistingWorkTargets(projects)
  if (targets.length === 0) {
    return { response: { text: copy.noExistingWork }, choices: [] }
  }

  if (targets.length === 1) {
    const target = targets[0]!
    // No id means the row cannot be opened. Say which work is meant rather
    // than firing an action that would fail inside the tool — and offer no
    // row, because a row without an id is a control that only looks live.
    if (!target.id) {
      return {
        response: {
          text: copy.chooseOne.replace('{choices}', formatUpdateExistingWorkTarget(target)),
        },
        choices: [],
      }
    }
    return {
      response: {
        text: copy.opening.replace('{name}', target.name),
        actions: [{ type: 'openProject', payload: { projectId: target.id } }],
      },
      choices: [],
    }
  }

  return {
    response: {
      text: copy.chooseOne.replace(
        '{choices}',
        targets.map(formatUpdateExistingWorkTarget).join(', '),
      ),
    },
    // Only rows that can actually be opened. A dated-but-idless row would
    // render a dead control, so it stays in the sentence and out of the list.
    choices: targets.filter((target) => target.id !== null),
  }
}
